define((require) => {
    "use strict";

    const _                     = require("underscore");
    const $                     = require("jquery");
    const Browser               = require("shared/services/Browser");
    const CkeditorFacade        = require("ui/experience/widgets/textWidget/textEditor/CkeditorFacade");
    const DomHandler            = require("ui/experience/widgets/textWidget/textEditor/DomHandler");
    const KeyCode               = require("shared/services/KeyCode");
    const Marionette            = require("marionette");
    const Preconditions         = require("shared/util/Preconditions");
    const {isBlank, dasherize}  = require("shared/util/StringUtils");
    const TextEditorConstants   = require("ui/experience/widgets/textWidget/textEditor/TextEditorConstants");
    const TextEditorViewHelpers = require("ui/experience/widgets/textWidget/textEditor/TextEditorViewHelpers");
    const WebFontsUtil          = require("shared/ui/experience/fonts/WebFontsUtil");

    const $body                 = $("body");
    const arrowKeys             = [KeyCode.UP, KeyCode.DOWN, KeyCode.LEFT, KeyCode.RIGHT];
    const stopPropagationOfKeys = [KeyCode.DELETE, KeyCode.BACK_SPACE].concat(arrowKeys);
    const MOUSEUP               = "mouseup";
    const KEY_Z                 = "z";
    const KEY_Y                 = "y";
    /**
     *  The inline editor wrapper for {@Link http://ckeditor.com/ ckeditor}
     *
     *  @event mouseup          Fires when the mouse up on the editor
     *  @event mousedown        Fires when the mouse down on the editor
     *  @event contentChange    Fires when content being changed including style changes
     *  @event editorBlur       Fires when edtior loses the focus
     *  @event arrowKeyUp       Fires when arrow keys up on the editor
     *
     *  @cfg {TextEditorModel} model    The editor model to facilitate the communication between
     *                                  property view and Ckeditor
     */
    return Marionette.View.extend({

        /**
         * A flag indicating whether the editor is editable
         */
        isEditable: false,

        _ckEditor: null,

        hasFakeHighlight: false,

        modelEvents: {
            "focus:propertyView": "applyFakeHighlight"
        },

        initialize(options) {
            this._contentChangeHandler = _.throttle(this._contentChangeHandler, 300);

            _.bindAll(
                this, "_mouseDownHandler", "_mouseUpHandler", "_onBlurHandler", "_keyDownHandler",
                "_keyUpHandler", "_contentChangeHandler", "_bodyMouseUpHandler"
            );

            this.$el = options.element
                .attr("contenteditable", false)
                .addClass("text-widget")
                .on(MOUSEUP, this._mouseUpHandler)
                .on("mousedown", this._mouseDownHandler)
                .on("blur",      this._onBlurHandler)
                .on("keydown",   this._keyDownHandler)
                .on("keyup",     this._keyUpHandler);

            this._ckEditor = new CkeditorFacade(options.element);
            this._ckEditor.on("change", this._contentChangeHandler);
            this._ckEditor.on("readOnly", this._onBlurHandler);

            // A hack to deal with the mouse up releasing outside the text editor
            // @see MOS-1819
            $body.on(MOUSEUP, this._bodyMouseUpHandler);
        },

        /**
         * Handles mouse down on events on the text widget element
         *
         * @param {mouseEvent} event The mouse event
         * @private
         */
        _mouseDownHandler(event) {
            const selectedWidgets = this.options.viewModel.selection.get("widgets");
            const singleSelection = selectedWidgets.length < 2; // Not a group selection
            const childElement = this.$el.find(event.target).length > 0; // An actual editable element and not the expanded border
            const modifierKeyDown = event.shiftKey || event.ctrlKey || event.metaKey; // Same keys as in FreeTransform.js#down

            if (childElement && singleSelection && !modifierKeyDown) {
                // Stop propagation if trying to edit or select content
                // otherwise it will drag the widget when making text selection.
                event.stopPropagation();
                this.enableEditable();
            } else {
                // If group selection or clicked outside of the editable elements disable editable
                // so the active state only is selected but not editable
                this.disableEditable();
            }

            if (singleSelection) {
                if (modifierKeyDown) {
                    // If shift is down the user is trying to multi select the widget
                    // add this widget to the widget selection
                    const newSelectedWidgets = _.uniq([this.options.textWidgetView].concat(selectedWidgets));
                    this.options.viewModel.selection.select(newSelectedWidgets);
                } else {
                    this.options.viewModel.selection.select([this.options.textWidgetView]);
                }
            }

            // Remove any fake highlight if there is any
            this._removeFakeHighlight();
            this.isDownInEditor = true;
        },

        /**
         * Handles mouseup events dispatched from the body node.
         * @param  {MouseEvent} event The mouse event instance.
         * @private
         */
        _mouseUpHandler(event) {
            this.isDownInEditor = false;
            this.trigger(MOUSEUP, event);
        },

        /**
         * Handles the event when mousedown happens inside the editor but mouseup release outside the editor.
         *
         * @see bug report MOS-1819
         * @private
         */
        _bodyMouseUpHandler() {
            if (this.isDownInEditor) {
                this._mouseUpHandler(event);
                this.isDownInEditor = false;
            }
        },

        /**
         * Handles key down events. If we don't stop propagation, it will trigger the parents key down events,
         * which will for example move the widget
         *
         * @param {keyEvent} event
         * @private
         */
        _keyDownHandler(event) {
            if (event.keyCode === KeyCode.RETURN) {
                this.trigger("newParagraph");
            }

            if (this.isEditable && stopPropagationOfKeys.indexOf(event.keyCode) !== -1) {
                event.stopPropagation();
            }

            if (event.ctrlKey && event.key) {
                if (event.key.toLowerCase() === KEY_Z || event.key.toLowerCase() === KEY_Y) {
                    // bypass check for fake highlighting already presenting, as during undo the fake highlighting spans will have been re-added without a check for hasFakeHighlight
                    this._ckEditor.removeFakeHighlight();
                }
            }

            if (!event.shiftKey) {
                // remove highlight
                this._removeFakeHighlight();
            }
        },

        /**
         * Handles key up events
         * @param {keyEvent} event
         * @private
         */
        _keyUpHandler(event) {
            const isArrowKey = _.contains(arrowKeys, event.keyCode);

            if (isArrowKey) {
                this.trigger("arrowKeyUp", event);
            }
        },

        /**
         * Throttled, see #initialize
         * @private
         */
        _contentChangeHandler() {
            this.trigger("contentChange");
        },

        _onBlurHandler(event) {
            this.trigger("editorBlur", event);
        },

        /**
         * Apply style to the current text selection
         *
         * @param {Object} changed   The changed properties
         * @param {Editor} model     The editor model
         */
        applyStyle(changed, model) {
            let prop;
            let nodeName;

            if (!this.isEditable) {
                return;
            }

            // The change of font family should always be accompanied with a provider, and possibly meta
            // data, so we put it into one object.
            if (changed.fontFamily) {
                changed = TextEditorViewHelpers.prepareFontFamily(changed, model, WebFontsUtil.getCssFontFamily(model));
            }

            _.each(changed, (value, property) => {
                if (property === "nodeName") {
                    nodeName = value || TextEditorConstants.TAGS.DIV;
                    this._ckEditor.applyStyle({nodeName, disableFallbackStyles: true});
                } else {
                    prop = dasherize(property);
                    if (_.contains(TextEditorConstants.INLINE_STYLES, prop)) {
                        nodeName = TextEditorConstants.TAGS.SPAN;
                    } else {
                        nodeName = model.has("nodeName") ? model.get("nodeName") : TextEditorConstants.TAGS.DIV;
                    }

                    this._ckEditor.applyStyle({property: prop, value, nodeName});
                }
            });
        },

        removeStyle(properties) {
            if (properties.nodeName) {
                delete properties.nodeName;
            }
            properties.forEach(this._ckEditor.removeStyle.bind(this._ckEditor));
        },

        /**
         * _setIsEditable Toggle the editable state of the editor.
         *
         * See `setIsEditable` in CkeditorFacade.js
         *
         * @param {Boolean} isEditable
         */
        _setIsEditable(isEditable) {
            Preconditions.isBoolean(isEditable);

            if (!isEditable) {
                this._removeFakeHighlight();
                this._ckEditor.deselectAll();
            }

            this.isEditable = isEditable;
            this._ckEditor.setIsEditable(isEditable);
        },

        /**
         * Enable the editable state, skip if it is already editable
         */
        enableEditable() {
            if (this.isEditable || !this._ckEditor) {
                return;
            }

            this._setIsEditable(true);

            // Bizarre FF and IE hack. it seems like the first focus will not make the cursor show up.
            if (Browser.type.firefox || Browser.type.ie) {
                this.$el.blur().focus();
            }
        },

        /**
         * Disable the editable state
         */
        disableEditable() {
            this._setIsEditable(false);
        },

        /**
         * Get the CSS style values for the current cursor/selection in the editor to update the property view.
         * Note: The selection can contain one DOM node or several nodes.
         *
         * @param {Object} properties    The style names that needs for values
         * @returns {*}
         */
        getCss(properties) {
            let elements;
            let highlightedElements;
            let result;
            let computedStyles;

            /**
             *  Defining which elements from which we need to extract the css.
             *
             *  If the widget is not in an editable state, we will run through
             *  every single element in the widget.
             *
             *  Otherwise we find the elements within the selection, or the
             *  single element that the cursor is touching.
             */
            if (!this.isEditable) {
                elements = DomHandler.getTextNodeParentList([this.$el[0]]);
            } else {
                highlightedElements = this._ckEditor.getSelectedElement();
                elements = DomHandler.getTextNodeParentList(highlightedElements);
            }

            // filter out elements that have been removed from the DOM.
            elements = elements.filter((element) => {
                return element && this.$el.find(element).length > 0;
            });

            /**
             * We begin by getting the font data. The font data consists of a
             * fontFamily (which is not always the same as the css font family
             * name), a fontProvider, and possibly some fontMetadata (which
             * include the cssFamilyNames).
             */
            result = TextEditorViewHelpers.getFontData(elements, this.el);
            properties = _.without(properties, "fontFamily");
            computedStyles = elements.map(Browser.computedStyle);

            return TextEditorViewHelpers.getCssValuesFromElements(result, properties, computedStyles);
        },

        getNodeName() {
            let elements;
            let allEqual = false;

            if (!this.isEditable) {
                elements = DomHandler.getTextNodeParentList([this.$el[0]]);
            } else {
                const highlightedElements = this._ckEditor.getSelectedElement();

                // filter out elements that have been removed from the DOM.
                elements = DomHandler.getTextNodeParentList(highlightedElements).filter((element) => {
                    return element && this.$el.find(element).length > 0;
                });
            }

            if (elements.length > 0) {
                allEqual = elements.every((val, i, arr) => val.nodeName === arr[0].nodeName);
            }

            if (elements.length === 1 || allEqual) {
                const node = this._findValidBlockElement(elements[0]);
                return node.nodeName.toLowerCase();
            }

            return null;
        },

        _findValidBlockElement(el) {
            if (el.nodeType === 3 || el.nodeName.toLowerCase() === TextEditorConstants.TAGS.SPAN) {
                return this._findValidBlockElement(el.parentNode);
            }

            return el;
        },

        /**
         * We only apply the style to the selection when it is in editable mode
         * And we don't apply style to empty selection, otherwise we end up with tons of empty span tags
         *
         * @returns {boolean|String|*}
         * @private
         */
        _canApplyStyle() {
            return this.isEditable && this._ckEditor.getSelectedText();
        },

        // apply fake highlight to the selection
        applyFakeHighlight() {
            if (this._canApplyStyle() && !this.hasFakeHighlight) {
                this._ckEditor.applyFakeHighlight();
                this.hasFakeHighlight = true;
            }
        },

        /**
         * Remove fake highlight
         */
        _removeFakeHighlight() {
            if (this.hasFakeHighlight) {
                this._ckEditor.removeFakeHighlight();
                this.hasFakeHighlight = false;
            }
        },

        /**
         * Returns the Html content the Text Widget.
         * Highlighting and unused tags are stripped out.
         *
         * @returns {String} Content of Text Widget
         */
        getContent() {
            // make a copy of the $el in a div container
            const $editorElCopy = $("<div></div>").append(this.$el.html());

            const $highlightSpans = $editorElCopy.find(`.${TextEditorConstants.HIGH_LIGHT_CLASS}`);

            // remove highlighted tags
            $highlightSpans.each((i, highlightSpanEl) => {
                highlightSpanEl.outerHTML = highlightSpanEl.innerHTML;
            });

            this._removeRedundantStyles($editorElCopy.get(0));
            this._removeEmptySpans($editorElCopy);

            return isBlank($editorElCopy.text()) ? "" : this._stripHtmlComments($editorElCopy.html());
        },

        /**
         * Traverses down a given tree removes redundant style definitions.
         * @param {HTMLElement} parentElement
         */
        _removeRedundantStyles(parentElement) {
            $(parentElement).children().each((i, childElement) => {
                if (childElement.tagName.toUpperCase() === "SPAN") {
                    for (let i = 0; i < childElement.style.length; i++) {
                        const propertyName = childElement.style[i];
                        // need to element.style[propertyName] as jQuery's $element.css(propertyName) takes the parent element property in Firefox if a child definition for that object isn't available.
                        if (childElement.style[propertyName] === parentElement.style[propertyName]) {
                            childElement.style[propertyName] = "";
                        }
                    }
                }
                this._removeRedundantStyles(childElement);
            });
        },

        _removeEmptySpans($parentElement) {
            $parentElement.find("span").each((i, spanEl) => {
                if (_.isUndefined(spanEl.innerText)) {
                    // the SPAN HTMLElement object still exists, but it's already been removed from the DOM (so `innerText` is undefined)
                    return;
                }

                // Remove any zero width space characters; these can break the layout.
                if (spanEl.innerText.match(/\u200B/)) {
                    spanEl.innerText = spanEl.innerText.replace(/\u200B/g, "");
                }

                if (spanEl.innerText === "") {
                    // remove empty span
                    $(spanEl).remove();
                    return;
                }

                // remove unstyled spans
                if (!spanEl.hasAttribute("style") || spanEl.style.length === 0 || (spanEl.hasAttribute("data-font-family") && !spanEl.style.fontFamily)) {
                    spanEl.outerHTML = spanEl.innerHTML;
                }
            });
        },

        /**
         * Takes a string (of html), and strips out html type comments.
         *
         * CKEditor sometimes (on windows only) inserts huge comment blocks
         * when pasting formatted text from emails or word. There is no reason
         * for us to save this in the backend.
         *
         * @private
         * @param {String} str  - The string with comment blocks.
         * @return {String}     - The filtered string.
         */
        _stripHtmlComments(str) {
            return str.replace(/<!--[\s\S]*?-->/g, "");
        },

        parseFont(elements) {
            return TextEditorViewHelpers.parseFont(elements, this.$el.parent().get(0));
        },

        editAllText() {
            this._ckEditor.selectAll();
            this.enableEditable();
        },

        onClose() {
            $body.off(MOUSEUP, this._bodyMouseUpHandler);
            this._ckEditor.close();
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/textWidget/textEditor/TextEditorView.js